import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsAIFCOnbardingEligible } from '@confluence/onboarding-ai-first-create/entry-points/useIsAIFCOnbardingEligible.ts';

import {
	useGetModernizeChangeboardingOnboardingState,
	useSetModernizeChangeboardingOnboardingState,
	STEPS,
} from '../../hooks/useModernizeChangeboardingOnboardingState.ts';
import { useModernizeChangeboardingTapTraits } from '../../hooks/useModernizeChangeboardingTapTraits.ts';
import type { SegmentHook } from '../../hooks/useCombinedSegments.tsx';

import { Spotlights } from './Spotlights.tsx';

export const useNav4GlobalSegment: SegmentHook = () => {
	const {
		loading: aifcLoading,
		error: aifcError,
		isAIFCOnbardingEligible,
	} = useIsAIFCOnbardingEligible();

	const {
		loading: hasSeenChangeboardingLoading,
		error: hasSeenChangeboardingError,
		hasNotSeenGlobalNav4Changeboarding,
	} = useHasNotSeenGlobalNav4Changeboarding();
	const { completeNav4GlobalSpotlight } = useSetModernizeChangeboardingOnboardingState();

	const isOnAnySettingsRoute = useRouteUrl(isOnAnySettingsRouteSelector);

	const showNav4GlobalSpotlight =
		!isAIFCOnbardingEligible && hasNotSeenGlobalNav4Changeboarding && !isOnAnySettingsRoute;

	if (hasSeenChangeboardingLoading || aifcLoading) {
		return {
			steps: [],
			onComplete: () => {},
			loading: true,
		};
	}

	const error = hasSeenChangeboardingError || aifcError;

	if (error) {
		return {
			steps: [],
			onComplete: () => {},
			error,
		};
	}

	if (!showNav4GlobalSpotlight) {
		return {
			steps: [],
			onComplete: () => {},
		};
	}

	const steps = [Spotlights.Nav4GlobalSpotlight];

	return {
		stepsToComplete: [STEPS.NAV_4_GLOBAL],
		steps,
		onComplete: completeNav4GlobalSpotlight,
	};
};

export const useHasNotSeenGlobalNav4Changeboarding = () => {
	const {
		loading: onboardingLoading,
		error: onboardingError,
		isNav4GlobalSpotlightDismissed,
	} = useGetModernizeChangeboardingOnboardingState();
	const {
		loading: tapLoading,
		error: tapError,
		isInNav4Audience,
		hasDismissedNav4ChangeboardingViaEP,
	} = useModernizeChangeboardingTapTraits();

	const loading = onboardingLoading || tapLoading;

	return {
		loading,
		error: onboardingError || tapError,
		hasNotSeenGlobalNav4Changeboarding:
			!loading &&
			isInNav4Audience &&
			!isNav4GlobalSpotlightDismissed &&
			!hasDismissedNav4ChangeboardingViaEP,
	};
};

const isOnAnySettingsRouteSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}

		return /\/wiki\/spaces\/[^/]+\/settings|\/wiki\/admin/.test(routeUrl);
	},
};
